import { useEffect, useMemo, useState } from 'react';
import { ShiftService } from '../services/shift.service';
import type { Shift } from '../interfaces/shift/Shift';
import { useAuth } from '../context/AuthContext';
import { usePermission } from './usePermission';

/**
 * Fetches the institution's shifts once and exposes a map for O(1) per-row lookup.
 * Returns:
 *   - shifts: Shift[] (raw list)
 *   - shiftById: (id: string | null | undefined) => Shift | null
 *   - loading
 */
export function useShiftLookup() {
    const { selectedInstitutionId } = useAuth();
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    // Shift lists are 403 for plain employees; don't call (avoids console/HTTP noise).
    const canViewShifts = usePermission('SHIFT_VIEW');
    const canViewRoster = usePermission('SHIFT_ROSTER_VIEW');
    const canView = canViewShifts || canViewRoster;

    useEffect(() => {
        let cancelled = false;
        if (!selectedInstitutionId || !canView) {
            setShifts([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        ShiftService.list(selectedInstitutionId)
            .then(list => { if (!cancelled) setShifts(list); })
            .catch(() => { if (!cancelled) setShifts([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [selectedInstitutionId, canView]);

    const byId = useMemo(() => {
        const m = new Map<string, Shift>();
        shifts.forEach(s => m.set(s.id, s));
        return m;
    }, [shifts]);

    const shiftById = (id: string | null | undefined): Shift | null => {
        if (!id) return null;
        return byId.get(id) ?? null;
    };

    return { shifts, shiftById, loading };
}
